import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, ExternalLink, Power } from "lucide-react";
import client from "@/api";
import { useAuth } from "@/context/AuthContext";
import { useTenant } from "@/context/TenantContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const blank = { name: "", custom_domain: "", subdomain: "", logo_url: "", primary_color: "#1B1F5B", secondary_color: "#F5B32B", admin_email: "", contact_info: { email: "", phone: "" }, active: true };

const TenantForm = ({ value, onDone, onCancel }) => {
  const root = useTenant().rootDomain;
  const [t, setT] = useState(value);
  const set = (k, v) => setT({ ...t, [k]: v });
  const submit = async (e) => {
    e.preventDefault();
    const body = { ...t, custom_domain: t.custom_domain || null, subdomain: t.subdomain || null, admin_email: t.admin_email || null };
    try {
      if (t.tenant_id) await client.put(`/admin/tenants/${t.tenant_id}`, body);
      else await client.post("/admin/tenants", body);
      toast.success("Tenant saved");
      onDone();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Check the form values");
    }
  };
  const f = (k, ph, id) => <Input placeholder={ph} value={t[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={id} />;
  return (
    <form onSubmit={submit} className="grid gap-3 rounded-2xl bg-white p-6 md:grid-cols-2" data-testid="tenant-form">
      {f("name", "Agency name", "tenant-name-input")}
      {f("admin_email", "Agency admin email (registered user)", "tenant-admin-email-input")}
      {f("custom_domain", "Custom domain e.g. www.agency.com", "tenant-domain-input")}
      {f("subdomain", `Subdomain e.g. agency (→ agency.${root})`, "tenant-subdomain-input")}
      {f("logo_url", "Logo URL", "tenant-logo-input")}
      <div className="flex gap-3">
        <Input type="color" className="w-20" value={t.primary_color} onChange={(e) => set("primary_color", e.target.value)} data-testid="tenant-primary-input" />
        <Input type="color" className="w-20" value={t.secondary_color} onChange={(e) => set("secondary_color", e.target.value)} data-testid="tenant-secondary-input" />
      </div>
      <Input placeholder="Support email" value={t.contact_info?.email || ""} data-testid="tenant-contact-email-input"
        onChange={(e) => set("contact_info", { ...t.contact_info, email: e.target.value })} />
      <Input placeholder="Support phone" value={t.contact_info?.phone || ""} data-testid="tenant-contact-phone-input"
        onChange={(e) => set("contact_info", { ...t.contact_info, phone: e.target.value })} />
      <div className="flex gap-3 md:col-span-2">
        <Button type="submit" className="bg-[#1B1F5B] hover:bg-[#141844]" data-testid="tenant-save-btn">Save tenant</Button>
        <Button type="button" variant="outline" onClick={onCancel} data-testid="tenant-cancel-btn">Cancel</Button>
      </div>
    </form>
  );
};

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const STATUS = { pending: ["Pending approval", "text-[#B57A00]"], rejected: ["Rejected", "text-red-500"] };

const TenantRow = ({ t, s, root, onEdit, onToggle, onDecide, onWallet }) => (
  <tr className="border-t border-slate-100" data-testid={`tenant-row-${t.tenant_id}`}>
    <td className="px-4 py-3">
      <div className="flex items-center gap-2">
        <span className="h-4 w-4 rounded-full" style={{ background: t.primary_color }} />
        <span className="h-4 w-4 rounded-full" style={{ background: t.secondary_color }} />
        <b className="text-[#1B1F5B]">{t.name}</b>
      </div>
    </td>
    <td className="px-4 py-3 text-slate-600">{t.custom_domain || "—"}<br /><span className="text-xs">{t.subdomain ? `${t.subdomain}.${root}` : ""}</span></td>
    <td className="px-4 py-3" data-testid={`tenant-status-${t.tenant_id}`}>
      {STATUS[t.status] ? <span className={STATUS[t.status][1]}>{STATUS[t.status][0]}</span>
        : t.active === false ? <span className="text-red-500">Inactive</span> : <span className="text-[#0E7C6B]">Active</span>}
      {t.kyc?.pan && <div className="text-xs text-slate-400">PAN {t.kyc.pan}{t.kyc.gst ? ` · GST ${t.kyc.gst}` : ""}</div>}
      {t.status === "pending" && (
        <div className="mt-1.5 flex gap-1.5">
          <Button size="sm" className="h-7 bg-[#0E7C6B] px-2.5 hover:bg-[#0A5348]" onClick={() => onDecide(t, "approve")} data-testid={`tenant-approve-${t.tenant_id}`}>Approve</Button>
          <Button size="sm" variant="outline" className="h-7 px-2.5" onClick={() => onDecide(t, "reject")} data-testid={`tenant-reject-${t.tenant_id}`}>Reject</Button>
        </div>
      )}
    </td>
    <td className="px-4 py-3" data-testid={`tenant-sales-${t.tenant_id}`}>{inr(s?.sales)}<br /><span className="text-xs text-slate-500">markup {inr(s?.markup_earned)}</span></td>
    <td className="px-4 py-3" data-testid={`tenant-settlement-${t.tenant_id}`}>
      <b className={(s?.net_settlement || 0) >= 0 ? "text-[#0E7C6B]" : "text-red-600"}>{inr(Math.abs(s?.net_settlement || 0))}</b>
      <br /><span className="text-xs text-slate-500">{(s?.net_settlement || 0) >= 0 ? "pay agency" : "collect from agency"}</span>
    </td>
    <td className="px-4 py-3">
      <b className="text-[#1B1F5B]" data-testid={`tenant-wallet-${t.tenant_id}`}>{inr(t.wallet)}</b>
      <div className="mt-1 flex gap-1">
        <Button size="sm" className="h-7 bg-[#0E7C6B] px-2 text-xs hover:bg-[#0A5348]" onClick={() => onWallet(t, "credit")} data-testid={`tenant-wallet-add-${t.tenant_id}`}>+ Add</Button>
        <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onWallet(t, "debit")} data-testid={`tenant-wallet-deduct-${t.tenant_id}`}>− Deduct</Button>
      </div>
    </td>
    <td className="px-4 py-3">
      <div className="flex gap-2">
        <Button size="sm" variant="outline" onClick={() => onEdit(t)} data-testid={`tenant-edit-${t.tenant_id}`}>Edit</Button>
        <a href={`/?tenant=${t.custom_domain || `${t.subdomain}.${root}`}`} target="_blank" rel="noreferrer" data-testid={`tenant-preview-${t.tenant_id}`}>
          <Button size="sm" variant="outline"><ExternalLink className="h-4 w-4" /></Button>
        </a>
        <Button size="sm" variant="outline" onClick={() => onToggle(t)} data-testid={`tenant-toggle-${t.tenant_id}`}><Power className="h-4 w-4" /></Button>
      </div>
    </td>
  </tr>
);

export default function TenantsManager() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [settle, setSettle] = useState({});
  const root = useTenant().rootDomain;
  const load = () => {
    client.get("/admin/tenants").then((r) => setItems(r.data.tenants));
    client.get("/admin/tenant-settlements").then((r) => setSettle(Object.fromEntries(r.data.settlements.map((x) => [x.tenant_id, x]))));
  };

  useEffect(() => {
    if (loading) return;
    if (user?.role !== "admin") { navigate("/login?tab=admin"); return; }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, loading]);

  const decide = async (t, action) => {
    try { await client.post(`/admin/tenants/${t.tenant_id}/${action}`); toast.success(action === "approve" ? `${t.name} is live` : "Application rejected"); load(); }
    catch { toast.error("Action failed"); }
  };

  const wallet = async (t, type) => {
    const v = window.prompt(`${type === "credit" ? "Add to" : "Deduct from"} ${t.name} wallet (₹):`);
    const amount = Number(v);
    if (!v || !(amount > 0)) return;
    const note = window.prompt("Note / reference (optional):") || "";
    try { const r = await client.post(`/admin/tenants/${t.tenant_id}/wallet`, { amount, type, note }); toast.success(`Wallet now ${inr(r.data.wallet)}`); load(); }
    catch (x) { toast.error(x?.response?.data?.detail || "Wallet update failed"); }
  };

  const toggle = async (t) => {
    await client.put(`/admin/tenants/${t.tenant_id}`, { ...t, active: t.active === false, admin_email: null });
    load();
  };

  return (
    <div className="min-h-screen bg-[#F5F7FB] px-4 py-10" data-testid="tenants-manager-page">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <Link to="/dashboard" className="text-sm text-slate-500 hover:text-[#1B1F5B]">← Admin dashboard</Link>
            <h1 className="mt-2 text-3xl font-bold text-[#1B1F5B]">White-label Agencies</h1>
            <p className="text-sm text-slate-500">Each agency gets its own domain, branding and isolated bookings.</p>
          </div>
          <Button onClick={() => setEditing(blank)} className="bg-[#F5B32B] text-[#1B1F5B] hover:bg-[#E89A10]" data-testid="tenant-add-btn">
            <Plus className="mr-1 h-4 w-4" />New agency
          </Button>
        </div>
        {editing && <TenantForm key={editing.tenant_id || "new"} value={editing} onCancel={() => setEditing(null)} onDone={() => { setEditing(null); load(); }} />}
        <div className="overflow-x-auto rounded-2xl bg-white">
          <table className="w-full text-left text-sm" data-testid="tenants-table">
            <thead className="bg-[#F5F7FB] text-xs uppercase text-slate-500">
              <tr><th className="px-4 py-3">Agency</th><th className="px-4 py-3">Domain</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Sales / Markup</th><th className="px-4 py-3">Net settlement</th><th className="px-4 py-3">Agency wallet</th><th className="px-4 py-3">Actions</th></tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={7} className="px-4 py-10 text-center text-slate-400">No agencies yet.</td></tr>}
              {items.map((t) => <TenantRow key={t.tenant_id} t={t} s={settle[t.tenant_id]} onEdit={(x) => setEditing({ ...x, admin_email: "" })} onToggle={toggle} onDecide={decide} onWallet={wallet} root={root} />)}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
